import Layout from '@/components/Layout';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { PremiseRecord } from '@/store/app';
import { ErrorRoute } from '@/components/ErrorRoute';
import z from 'zod';
import { queries } from '@/utils/queryOptions';
import { getIsDemoDataCookie } from '@/server/cookies';
import { getSessionFn } from '@/server/session';

export const Route = createFileRoute('/_protected')({
	validateSearch: z.object({
		sectionId: z.string().catch('NONE'),
		demoMode: z.boolean().optional(),
	}),
	component: Layout,
	errorComponent: ({ ...rest }) => <ErrorRoute showButtons={true} {...rest} />,
	pendingComponent: () => 'LOADING',
	beforeLoad: async ({ context }) => {
		const session = await getSessionFn();
		const isDemo = await getIsDemoDataCookie();

		if (!session && !isDemo) throw redirect({ to: '/auth/login' });

		try {
			const { items: sectionsList } = await context.queryClient.ensureQueryData(
				queries.sections.list(isDemo),
			);
			const { items: roomTypesList } =
				await context.queryClient.ensureQueryData(
					queries['room-types'].list(isDemo),
				);

			const sections: PremiseRecord = {};
			const premises: PremiseRecord = {};
			const floors: PremiseRecord = {};
			const rooms: PremiseRecord = {};
			const roomTypes: PremiseRecord = roomTypesList.reduce((acc, item) => {
				acc[item.id] = { ...item, parentId: 'none' };
				return acc;
			}, {} as PremiseRecord);

			// section to more convinient object
			for (const section of sectionsList) {
				sections[section.id] = {
					name: section.name,
					id: section.id,
					parentId: section.id,
				};
				for (const premise of section.premises) {
					premises[premise.id] = {
						name: premise.name,
						id: premise.id,
						parentId: section.id,
					};
					for (const floor of premise.floors) {
						floors[floor.id] = {
							name: floor.name,
							id: floor.id,
							parentId: premise.id,
						};
						for (const room of floor.rooms) {
							rooms[room.id] = {
								name: room.name,
								id: room.id,
								parentId: floor.id,
							};
						}
					}
				}
			}

			return {
				sections,
				premises,
				floors,
				rooms,
				roomTypes,
			};
		} catch (_) {
			throw new Error('Unable to find sections');
		}
	},
	loaderDeps: ({ search }) => ({ sectionId: search.sectionId }),
	loader: ({ context, deps }) => {
		if (deps.sectionId === 'NONE' || !deps.sectionId) {
			const section = Object.values(context.sections)[0];
			if (!section) throw new Error('No authorized sections found.');
			else
				throw redirect({
					search: { sectionId: section.id },
				});
		}

		// Invalid section id
		const section = context.sections[deps.sectionId];
		if (!section) throw redirect({ to: '/' });
	},
});
